import { getSortParams } from '@/utils/utils';
import { request } from '@umijs/max';
import { SortOrder } from 'antd/es/table/interface';

const sponsorLeaderboardService = {
  async updateSnapshotWager(params: {
    leaderboardId: string;
    snapshotId: string;
    newWagered: number;
    abuseCoefficient?: number;
    modifyWagered?: number;
    modifyDisplayedWagered?: number;
  }) {
    const { leaderboardId, snapshotId, ...data } = params;
    return request(
      `/admin/sponsor/${leaderboardId}/snapshots/${snapshotId}/wager`,
      {
        method: 'PUT',
        data,
      },
    );
  },

  getAllSponsorLeaderboards: async (options?: { [key: string]: any }) => {
    const response = await request<API.Service.SponsorLeaderboard[]>(
      '/admin/sponsor-leaderboard/all',
      {
        method: 'GET',
        ...(options || {}),
      },
    );

    return response;
  },

  async getSponsorLeaderboardsBySponsorId(
    params: {
      sponsorId: string;
      pageSize?: number;
      current?: number;
    },
    sorter?: Record<string, SortOrder>,
    options?: { [key: string]: any },
  ): Promise<any> {
    const url = `/admin/sponsor-leaderboard`;

    const sort = getSortParams(sorter);

    const current = params.current || 1;
    const pageSize = params.pageSize || 20;

    const response = await request<API.Service.SponsorLeaderboard[]>(url, {
      method: 'GET',
      params: {
        page: current - 1,
        size: pageSize,
        sponsorId: params.sponsorId,
        ...(sort ? { sort } : {}),
      },
      getResponse: true,
      ...(options || {}),
    });

    return {
      data: response.data?.map((item, index) => ({
        ...item,
        serialNumber: (current - 1) * pageSize + index + 1,
      })),
      total: parseInt(response.headers['x-total-count'], 10),
      success: true,
    };
  },

  async createSponsorLeaderboard({
    body,
    options,
  }: {
    body: API.Service.SponsorLeaderboardForm;
    options?: { [key: string]: any };
  }) {
    const response = await request('/admin/sponsor-leaderboard', {
      method: 'POST',
      data: body,
      ...(options || {}),
    });

    return response;
  },

  async updateSponsorLeaderboard({
    body,
    options,
  }: {
    body: API.Service.SponsorLeaderboardForm;
    options?: { [key: string]: any };
  }) {
    const response = await request(`/admin/sponsor-leaderboard`, {
      method: 'PUT',
      data: body,
      ...(options || {}),
    });

    return response;
  },

  async deleteSponsorLeaderboard({ id }: { id: string }) {
    const response = await request(`/admin/sponsor-leaderboard/${id}`, {
      method: 'DELETE',
    });

    return response;
  },

  async getSponsorLeaderboard({ id }: { id: string }) {
    const response = await request<API.Service.SponsorLeaderboard>(
      `/admin/sponsor-leaderboard/${id}`,
      {
        method: 'GET',
      },
    );

    return response;
  },

  async endSponsorLeaderboard({
    id,
    body,
  }: {
    id: string;
    body: API.Service.EndSponsorLeaderboardForm;
  }) {
    const response = await request(`/admin/sponsor-leaderboard/${id}/end`, {
      method: 'POST',
      params: body,
    });

    return response;
  },

  async getSponsorLeaderboardPrizes({
    leaderboardId,
  }: {
    leaderboardId: string;
  }) {
    const response = await request<API.Service.SponsorLeaderboardPrize[]>(
      `/admin/sponsor-leaderboard/${leaderboardId}/prize?size=100`,
      {
        method: 'GET',
      },
    );

    return response;
  },

  async addSponsorLeaderboardPrize({
    leaderboardId,
    body,
  }: {
    leaderboardId: string;
    body: API.Service.SponsorLeaderboardPrizeForm;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/prize`,
      {
        method: 'POST',
        data: body,
      },
    );

    return response;
  },

  async updateSponsorLeaderboardPrize({
    leaderboardId,
    body,
  }: {
    leaderboardId: string;
    body: API.Service.SponsorLeaderboardPrizeForm;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/prize`,
      {
        method: 'PUT',
        data: body,
      },
    );

    return response;
  },

  async deleteSponsorLeaderboardPrize({
    leaderboardId,
    prizeId,
  }: {
    leaderboardId: string;
    prizeId: string;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/prize/${prizeId}`,
      {
        method: 'DELETE',
      },
    );

    return response;
  },

  async importSponsorLeaderboardPrizeFromSponsor({
    leaderboardId,
  }: {
    leaderboardId: string;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/prize/import-from-sponsor`,
      {
        method: 'POST',
      },
    );

    return response;
  },

  async getSponsorLeaderboardDefinition({
    leaderboardId,
  }: {
    leaderboardId: string;
  }) {
    const response = await request<API.Service.SponsorLeaderboardDefinition[]>(
      `/admin/sponsor-leaderboard/${leaderboardId}/item-definition`,
      {
        method: 'GET',
      },
    );

    return response?.sort((a, b) => a.value - b.value);
  },

  async addSponsorLeaderboardDefinition({
    leaderboardId,
    body,
  }: {
    leaderboardId: string;
    body: API.Service.SponsorLeaderboardDefinition;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/item-definition`,
      {
        method: 'POST',
        data: body,
      },
    );

    return response;
  },

  async updateSponsorLeaderboardDefinition({
    leaderboardId,
    body,
  }: {
    leaderboardId: string;
    body: API.Service.SponsorLeaderboardDefinition;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/item-definition`,
      {
        method: 'PUT',
        data: body,
      },
    );

    return response;
  },

  async deleteSponsorLeaderboardDefinition({
    leaderboardId,
    id,
  }: {
    leaderboardId: string;
    id: number;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/item-definition/${id}`,
      {
        method: 'DELETE',
      },
    );

    return response;
  },

  async importSponsorLeaderboardRewardFromSponsor({
    leaderboardId,
  }: {
    leaderboardId: string;
  }) {
    const response = await request(
      `/admin/sponsor-leaderboard/${leaderboardId}/item-definition/import-from-sponsor`,
      {
        method: 'POST',
      },
    );

    return response;
  },

  async getLeaderboardUsers(
    params: {
      id: string;
      // page: number;
      // size: number;

      search?: string;
      pageSize?: number;
      current?: number;
      // sortColumn?: string | null;
      // sortOrder?: string;
      // sort?: string;
    },
    sorter?: Record<string, SortOrder>,
    options?: { [key: string]: any },
  ): Promise<any> {
    const url = `/admin/sponsor-leaderboard/${params.id}/users`;

    const sort = getSortParams(sorter);

    const current = params.current || 1;
    const pageSize = params.pageSize || 20;

    const response = await request<API.Service.SponsorLeaderboardUser[]>(url, {
      method: 'GET',
      params: {
        page: current - 1,
        size: pageSize,
        search: params.search,
        ...(sort ? { sort } : {}),
      },
      getResponse: true,
      ...(options || {}),
    });

    return {
      data: response.data?.map((item, index) => ({
        ...item,
        serialNumber: (current - 1) * pageSize + index + 1,
      })),
      total: parseInt(response.headers['x-total-count'], 10),
      success: true,
    };
  },

  /**
   * 获取 Previous Leaderboard 的快照数据
   */
  async getLeaderboardSnapshots(
    params: { leaderboardId: string; search?: string; current?: number; pageSize?: number },
    sorter?: Record<string, any>,
  ) {
    const { leaderboardId, search, current, pageSize } = params;
    return request(`/admin/sponsor-leaderboard/${leaderboardId}/snapshots`, {
      method: 'GET',
      params: {
        search,
        page: current ? current - 1 : 0,
        size: pageSize || 20,
      },
    });
  },

  async getLeaderboardUsers2(
    params: {
      sponsorId: string;
      leaderboardId: string;
      // page: number;
      // size: number;

      search?: string;
      pageSize?: number;
      current?: number;
      // sortColumn?: string | null;
      // sortOrder?: string;
      // sort?: string;
    },
    sorter?: Record<string, SortOrder>,
    options?: { [key: string]: any },
  ): Promise<any> {
    const url = `/admin/sponsor-leaderboard/${params.sponsorId}/${params.leaderboardId}/users2`;

    const sort = getSortParams(sorter);

    const current = params.current || 1;
    const pageSize = params.pageSize || 20;

    const response = await request<API.Service.SponsorLeaderboardUser2[]>(url, {
      method: 'GET',
      params: {
        page: current - 1,
        size: pageSize,
        search: params.search,
        ...(sort ? { sort } : {}),
      },
      getResponse: true,
      ...(options || {}),
    });

    return {
      data: response.data?.map((item, index) => ({
        ...item,
        serialNumber: (current - 1) * pageSize + index + 1,
      })),
      total: parseInt(response.headers['x-total-count'], 10),
      success: true,
    };
  },
};

export default sponsorLeaderboardService;
